// enums
import { SelfMatchAction } from 'components/enums';

// types
import type { OpenRequestResponse } from 'RestAPIs/roster/open-requests/types';
import type { OpenRequestFunctionGroup } from '../RosterPlan.types';

/**
 * The grouping key for one open request row.
 *
 * A function is not one group but one group *per status*: the same function shows up separately as
 * open, as sent to flex, as a roster match and so on, which is why the status is folded into the id
 * rather than kept beside it. Only the plain-open case keeps the bare numeric `functionId`, and the
 * sort below leans on exactly that to put open work first.
 */
export const getFunctionId = ({
  statusCode,
  functionId
}: {
  statusCode: string;
  functionId: number | string;
}): string | number => {
  switch (statusCode) {
    case 'rosmt':
      return `${functionId}-roster-match`;
    case 'send':
      return `${functionId}-send-to-flex`;
    case 'park':
      return `${functionId}-parked`;
    case 'self':
      return `${functionId}-self`;
    case 'openForSelfMatch':
      return `${functionId}-openForSelfMatch`;
    case 'self-openForSelfMatch':
      return `${functionId}-self-openForSelfMatch`;
    default:
      return functionId;
  }
};

/**
 * Open work first, then everything that is not a roster match or a flex send, then roster matches,
 * then flex sends. Within one class the groups keep the order their first row appeared in.
 */
const getOrderValue = ({ functionId }: OpenRequestFunctionGroup) => {
  if (typeof functionId === 'number') return 0;
  if (functionId.includes('-roster-match')) return 2;
  if (functionId.includes('-send-to-flex')) return 3;
  return 1;
};

const byStatusThenInsertion = (
  a: OpenRequestFunctionGroup,
  b: OpenRequestFunctionGroup
) => {
  const orderA = getOrderValue(a);
  const orderB = getOrderValue(b);

  return orderA !== orderB ? orderA - orderB : a.order - b.order;
};

/**
 * Collapses the open requests into one row per function-and-status, each carrying the individual
 * requests as `days`.
 *
 * `unnamedFunctionLabel` is passed in rather than translated here, so this stays a pure function:
 * the caller owns `t`.
 *
 * The status flags are taken from the *first* row that creates a group and are not revisited for
 * the rows that join it afterwards — the behaviour of the code this replaces, kept deliberately.
 *
 * It is observable. A subscriber rewrites the key to `-openForSelfMatch` whatever the row's own
 * status was, so one such group can hold a `park` row next to an `open` row; whichever came first
 * decides `isParked` for the whole group.
 */
export const groupOpenRequestsByFunction = ({
  openRequests,
  unnamedFunctionLabel
}: {
  openRequests: OpenRequestResponse[];
  unnamedFunctionLabel: string;
}): OpenRequestFunctionGroup[] =>
  openRequests
    .reduce<OpenRequestFunctionGroup[]>((groups, openRequest, index) => {
      const isSendToFlex = openRequest.statusCode === 'send';
      const isRosterMatch = openRequest.statusCode === 'rosmt';
      const isParked = openRequest.statusCode === 'park';
      const isSelf = openRequest.statusCode === 'self';
      const isOpenForSelfMatch =
        openRequest.employeeCostCenterActions?.length > 0 &&
        openRequest.employeeCostCenterActions.some(
          (item) => item.action === SelfMatchAction.SUBSCRIBE
        );
      const isSelfAndOpenForSelfMatch = isSelf && isOpenForSelfMatch;

      const functionId = getFunctionId({
        statusCode: isSelfAndOpenForSelfMatch
          ? 'self-openForSelfMatch'
          : isOpenForSelfMatch
          ? 'openForSelfMatch'
          : openRequest.statusCode,
        functionId: openRequest.functionId
      });

      const existingGroup = groups.find(
        (group) => group.functionId === functionId
      );

      if (existingGroup) {
        existingGroup.days.push(openRequest);
        return groups;
      }

      groups.push({
        functionId,
        functionName: openRequest.functionName || unnamedFunctionLabel,
        days: [openRequest],
        isSendToFlex,
        isOpenForSelfMatch,
        isSelfAndOpenForSelfMatch,
        isRosterMatch,
        isParked,
        isSelf,
        functionColor: openRequest.functionColor,
        order: 1 + index
      });

      return groups;
    }, [])
    .sort(byStatusThenInsertion);
